//================================================================
//  Component: Add Application
//================================================================

//  Purpose: Add a new application to the applications collection

//  Properties:
//    - None

//  AddApplication:
//    <AddApplication></AddApplication>    

//================================================================


//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser, SetToast } from '../../../../Library/GlobalContexts';

//Components
import UserSearchRHF from '../../../../Components/UserSearchRHF/UserSearchRHF';
import LookUpRHF from '../../../../Components/LookUpRHF/LookUpRHF';

//Functions
import WriteDocument from '../../../../Library/WriteDocument';

// Images
import IconAdd from '../../../../Components/Images/Icon_Add_White.svg';


export default function AddApplication() {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Open or Close the 'Add Application' side pane
    const [addApplication, setAddApplication] = useState(false);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    // https://react-hook-form.com/get-started
    const {
        register,
        setValue,
        handleSubmit,
        reset,
        clearErrors,
        setError,
        formState: { isSubmitSuccessful, errors },
    } = useForm();

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------


    // Submit Handler
    const onSubmit = (formData) => {

        //------------------------------------------------------
        //  Validation Checks
        //------------------------------------------------------

        let preventSubmit = false;

        // Check the Vendor Lookup is populated
        if (formData.vendorname?.length > 0 && (formData['vendorname-vendorid'] === undefined || formData['vendorname-vendorid'] === '')) {
            preventSubmit = true;
            setError('vendorname', {
                type: 'required',
                message: 'This field is required.'
            });
        }

        // Check User Selected for All Required User Search Fields
        //  - Business Owner
        if (formData['businessownergivenname'] === undefined || formData['businessownergivenname'] === '') {
            preventSubmit = true;
            setError('businessowner', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        if (formData['businessownersurname'] === undefined || formData['businessownersurname'] === '') {
            preventSubmit = true;
            setError('businessowner', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        //  - IT Owner
        if (formData['itownergivenname'] === undefined || formData['itownergivenname'] === '') {
            preventSubmit = true;
            setError('itowner', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        if (formData['itownersurname'] === undefined || formData['itownersurname'] === '') {
            preventSubmit = true;
            setError('itowner', {
                type: 'required',
                message: 'This field is required.'
            })
        }

        // Check if ALL validations have passed
        if (preventSubmit === true) return;

        // Open the toast
        setToast({
            'type': 'pending',
            'message': 'Preparing application',
        });

        // ============================
        // Create a searcharray for lookup fields
        // ============================

        const searchArray = [];
        let previousValue = '';
        [...formData.applicationname]?.forEach((value, index) => {

            // Skip the first character
            if (index === 0) {

                previousValue = value;
                return searchArray.push(value.toLowerCase());

            }

            // Create an array of each character
            searchArray.push(`${previousValue}${value}`.toLowerCase());
            previousValue = `${previousValue}${value}`.toLowerCase();

        });

        // ============================
        // Firestore Doc
        // ============================

        const documentObject = {
            'applicationid': `${btoa(formData.applicationname)}-${Date.now()}`,
            'linkedrequests': {},
            'applicationstatus': formData.applicationstatus,

            'createdby': getUser.emailaddress, // Email Address
            'createdbyname': `${getUser.givenname} ${getUser.surname}`, // Full Name (i.e. Nowshin Hassan)
            'createddate': new Date(),
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname,
            },

            'agreements': [],
            'applicationname': formData.applicationname,
            'searcharray': searchArray,
            'applicationdescription': formData.applicationdescription,
            'vendor': {
                'vendorname': formData.vendorname,
                'vendorid': formData['vendorname-vendorid'],
            },
            'itowner': [
                formData.itowner
            ],
            'businessowner': [
                formData.businessowner
            ],
            'defaultapp': [],
            'usedby': [],
            'subscribed': [],
            'nooflicensespurchased': '0',
            'type': '',
            'deploymentname': '',
            'deploymenttype': [],
            'capabilityfamily': [],
            'supportedby': formData.supportedby,
            'supportlevelinternal': [],
            'supportlevelexternal': [],
            'ssoimplemented': '',
            'mfaimplemented': '',
            'typeofcost': '',
            'budgetowner': [],
            'projectcode': '',
            'tasknumber': '',
            'expendituretype': '',
            'licencetype': '',
            'costallocationcurrency': '',
            'bcp': '',
            'rpo': '',
            'rto': '',
            'linktodr': '',
            'itmanaged': '', // Yes or No
            'itcommerciallymanaged': '', // Yes or No
            'itbudgeted': '', // Yes or No
            'primarybeneficiary': '',
            'region': [],
            'applicationcategories': [],

            'environment': [],   // 'Prod', 'UAT/pre-prod', 'SIT', 'Dev', 'DR'
            'appserver': '',
            'otherserver': '',
            'softwarerequirements': '',
            'dnsnames': [],
            'serviceaccounts': [],
            'gcpproject': [],
            'primaryhostinglocation': '',
            'secondaryhostinglocation': '',
            'applicationurl': '',
            'osversion': '',
            'mtohours': '', //  Choice - 'Tier 0', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5', 'Tier 6'  
            'drfrequency': '', // Choice -  'Annually, Semi-Annually
            'applicablecontrols': '',
            'dataclassification': '', // Choice - 'Public', 'Internal', 'Confidential', 'Restricted'
            'contractenddate': null,//agreement?.agreementenddate,
            'numberofusers': '', // Number
            'visibleinjsm': '', // Choice - 'Yes', 'No'
            'supportassignmentgroup': '',
            'supportrenewaldate': '',
            'workflows': '',
            'useraccessreview': '', // Choice - 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
            'prodbackup': '', // Choice - 'Daily', 'Weekly', 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
            'slareporting': '', // Choice - 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
            'supportdl': '',
            'decommissiondata': {
                'decommissiondate': null,
                'dataretention': '',
                'dataclassification': '',
                'datalocation': '', // Choice - 'Sharepoint', 'DAS', 'Common Vault', 'tape'
            },
            'nextcompreviewdate': null, // Date
            'groups': [],
            'assettype': '', // Application, Technology, Interface / Integration & Information
            'confidentialityrating': '', // Choice - 1 to 5
            'integrityrating': '', // Choice - 1 to 5
            'availabilityrating': '', // Choice - 1 to 5
            'assetscore': '',  // 0 to 100
            'sensitivityrating': '', // Choice - 1 to 5
            'assetier': '', // Choice - 1 to 5
            'internetfacing': '', // Choice - Yes, No & N/A 
            'mva': '', // Yes & No
        };

        WriteDocument('applications', documentObject.applicationid, documentObject, false)
            .then(() => {

                // Reset form & close side pane
                setAddApplication(false);

                // Show Success Toast
                setToast(
                    {
                        'type': 'success',
                        'message':
                            <>
                                Application added. <a href={process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory/application/${documentObject?.applicationid}?view=overview` : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? `https://uatinventory.digitalservices.lendlease.com/inventory/application/${documentObject?.applicationid}?view=overview` : `https://inventory.digitalservices.lendlease.com/inventory/application/${documentObject?.applicationid}?view=overview`} target='_blank' rel='noopener noreferrer'>{documentObject?.applicationname}</a>
                            </>
                    }
                );

            })
            .catch((error) => {

                console.log('error', error);

                // Reset form & close side pane
                setAddApplication(false);

                // Set error on toast
                setToast({
                    'type': 'error', // info, pending, success, warning, error
                    'message': 'Failed to create application',
                });


            });

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    useEffect(() => {

        if (isSubmitSuccessful) {
            reset();
        };
        if (addApplication === false) return;

        // eslint-disable-next-line 
    }, [isSubmitSuccessful, reset, addApplication]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (addApplication === false) return (

        <button className='Primary-Button whitespace-nowrap flex flex-row gap-2' onClick={() => setAddApplication(true)}>
            <img src={IconAdd} alt='Add'></img> Add Application
        </button>

    );

    return (
        <>

            {/* ======== Keeps the button is visible during slide animation ========= */}
            <button className='Primary-Button whitespace-nowrap flex flex-row gap-2' onClick={() => setAddApplication(true)}>
                <img src={IconAdd} alt='Add'></img> Add Application
            </button>

            {/* =========================================================== */}
            {/*  Add Application Pane                                         */}
            {/* =========================================================== */}

            <div className='Pane-Background'>
                <dialog className='Pane-Container'>

                    {/*  ========= Add Application Form * ========= */}
                    <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Add Application  </h4>
                        </div>

                        <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/*  ========= 1. Application Name * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>1. Application Name <span className='text-[#C4314B]'>*</span></label>
                                <input
                                    className={errors.applicationname ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    type='text'
                                    placeholder='Enter application name'
                                    autoComplete='off'
                                    {...register(
                                        'applicationname', {
                                        required: 'This is a required field.',
                                        maxLength: { value: 100, message: 'Name cannot exceed 100 characters' },
                                    })}
                                ></input>
                                {errors.applicationname && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.applicationname?.message}</p>}
                            </div>

                            {/*  ========= 2. Application Description * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>2. Application Description <span className='text-[#C4314B]'>*</span></label>
                                <textarea
                                    className={errors.applicationdescription ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                                    type='text'
                                    placeholder='Enter description'
                                    autoComplete='off'
                                    {...register(
                                        'applicationdescription', {
                                        required: 'This is a required field.',
                                        maxLength: 500,
                                    })}
                                ></textarea>
                                {errors.applicationdescription && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.applicationdescription?.message}</p>}
                            </div>

                            {/*  ========= 3. Application Status * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'> 3. Application Status <span className='text-[#C4314B]'>*</span></label>
                                <select
                                    className='Input-Field-Select'
                                    {...register(
                                        'applicationstatus', {
                                        required: 'This is a required field.'
                                    })
                                    } >
                                    <option value='Draft'>Draft</option>
                                    <option value='Active'>Active</option>
                                    <option value='Divest'>Divest</option>
                                    <option value='Dormant'>Dormant</option>
                                    <option value='Archived'>Archived</option>
                                    <option value='Decommissioned'>Decommissioned</option>
                                </select>
                            </div>

                            {/*  ========= 4. Vendor Name * ========= */}
                            <div className='FormComponent-Row'>

                                <label className='font-medium'>4. Vendor Name <span className='text-[#C4314B]'>*</span></label>
                                <LookUpRHF
                                    fieldId={'vendorname'}
                                    collectionId={'vendors'}
                                    dataField={'vendorname'}
                                    placeholder={'Search vendor by name'}
                                    required={true}
                                    register={register}
                                    setValue={setValue}
                                    errors={errors.vendorname}
                                    setError={setError}
                                    dataFieldSecondary={'tradingas'}
                                    dataFieldTertiary={'vendorid'}
                                    additionalQueries={['vendorstatus', '==', 'Active']}
                                    createNewHTML={
                                        <span className='font-normal text-[#A0A0A0]'>
                                            Can't find what you're looking for? Click <a href={process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? 'https://devinventory.digitalservices.lendlease.com/inventory?view=vendors' : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? 'https://uatinventory.digitalservices.lendlease.com/inventory?view=vendors' : 'https://inventory.digitalservices.lendlease.com/inventory?view=vendors'} target='_blank' rel='noopener noreferrer'>here</a>.
                                        </span>}
                                    clearErrors={clearErrors}
                                ></LookUpRHF>

                            </div>

                            {/*  ========= 5. Business Owner * ========= */}
                            <div className='FormComponent-Row'>

                                <label className='font-medium'>5. Business Owner <span className='text-[#C4314B]'>*</span></label>
                                <UserSearchRHF
                                    dataField={'businessowner'}
                                    required={true}
                                    register={register}
                                    setValue={setValue}
                                    clearErrors={clearErrors}
                                    setError={setError}
                                    errors={errors.businessowner}
                                ></UserSearchRHF>

                            </div>

                            {/*  ========= 6. IT Owner * ========= */}
                            <div className='FormComponent-Row'>

                                <label className='font-medium'>6. IT Owner <span className='text-[#C4314B]'>*</span></label>
                                <UserSearchRHF
                                    dataField={'itowner'}
                                    required={true}
                                    register={register}
                                    setValue={setValue}
                                    clearErrors={clearErrors}
                                    setError={setError}
                                    errors={errors.businessowner}
                                ></UserSearchRHF>

                            </div>

                            {/*  ========= 7. Supported by * ========= */}
                            <div className='FormComponent-Row'>
                                <label className='font-medium'> 7. Supported by <span className='text-[#C4314B]'>*</span></label>
                                <select
                                    className='Input-Field-Select'
                                    {...register(
                                        'supportedby', {
                                    })
                                    } >
                                    <option value='Accenture'>Accenture</option>
                                    <option value='Business'>Business</option>
                                    <option value='Deloitte'>Deloitte</option>
                                    <option value='Lendlease - Internal'>Lendlease - Internal</option>
                                    <option value='Lendlease - Digital'>Lendlease - Digital</option>
                                    <option value='Digitality'>Digitality</option>
                                    <option value='Hype & Dexter'>Hype & Dexter</option>
                                    <option value='DataProwess'>DataProwess</option>
                                    <option value='Other'>Other</option>
                                </select>
                            </div>

                        </div>

                        {/*  ========= Button Container ========= */}
                        <div className='flex flex-row gap-2 px-4 py-3'>
                            {/* Submit */}
                            <button
                                className='Primary-Button whitespace-nowrap'
                                disabled={false}
                                type='submit'
                                value='submit'
                            >
                                Submit
                            </button>

                            {/* Cancel */}
                            <button className='Secondary-Button whitespace-nowrap' onClick={() => {

                                // close side pane
                                reset();
                                setAddApplication(false);

                            }}>Cancel</button>

                        </div>

                    </form>

                    {errors.applications && <p className='font-medium text-sm text-[var(--darkred)] my-2'>{errors.applications?.message}</p>}

                </dialog>
            </div>
        </>
    )
}

